import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import {Handle, HandleType, Position, useEdges} from 'reactflow';

import {isMaterialConnectionCompatible} from 'procedures/design/materialEdgeUtils';
import {ProcedureAccessLevel} from 'procedures/design/validation/determineProcedureAccessLevel';
import {
  isMaterialOutputSettingComponent,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {FieldType, isRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {isHandleConnectedToInvalidMaterialEdge, isValidConnection} from './handleValidationUtils';
import {ProcedureEditorContext} from './ProcedureEditorContext';
import {nodeZIndex} from './procedureMethodNodeCommonConsts';
import {ProcedureValidationContext} from './validation/ProcedureValidationContext';

interface BaseProcedureHandleProps {
  id: string;
  isVisible: boolean;
}

type ProcedureMaterialOutputHandleProps = BaseProcedureHandleProps & {
  materialOutputSetting: ProcedureMaterialOutputSetting;
};

type ProcedureMaterialInputHandleProps = BaseProcedureHandleProps & {
  materialInputSetting: ProcedureMaterialInputSetting;
};

type ProcedureGenericHandleProps = BaseProcedureHandleProps & {
  type: HandleType;
};

export function ProcedureMaterialOutputHandle(props: ProcedureMaterialOutputHandleProps): JSX.Element {
  const {handlesInUse, invalidMaterialHandles, isProcedureReadOnly} = ProcedureEditorContext.useContext();
  const {accessLevel} = ProcedurePageContext.useContext();
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();

  const edges = useEdges();
  const isInvalidMaterialEdgeHandle = useMemo(
    () =>
      invalidMaterialHandles.includes(props.id) ||
      isHandleConnectedToInvalidMaterialEdge(props.id, edges, procedureValidationErrors),
    [edges, invalidMaterialHandles, procedureValidationErrors, props.id]
  );

  const handleClass = css(
    styles.handle,
    !props.isVisible && styles.invisibleHandle,
    handlesInUse.includes(props.id) && styles.connectedHandle,
    isInvalidMaterialEdgeHandle && styles.invalidHandle
  );
  return (
    <Handle
      type="source"
      position={Position.Right}
      className={handleClass}
      isConnectable={!isProcedureReadOnly && accessLevel === ProcedureAccessLevel.FULL_ACCESS}
      isValidConnection={(connection) => isValidConnection(connection, handlesInUse)}
      id={props.id}
      {...getTestProps({component: 'ProcedureMaterialOutputHandle', element: 'handle', key: props.id})}
    />
  );
}

function getMaterialDataTypeText(
  materialSetting: ProcedureMaterialOutputSetting | ProcedureMaterialInputSetting
): string {
  if (isMaterialOutputSettingComponent(materialSetting)) {
    return materialSetting.materialOutputVersion.dataType.requiredLink.tagSchema!.name;
  } else {
    if (materialSetting.materialInputVersion.dataType.type === FieldType.TEXT) {
      return 'Text';
    } else if (isRequiredLinkTagSchema(materialSetting.materialInputVersion.dataType.requiredLink)) {
      return materialSetting.materialInputVersion.dataType.requiredLink.tagSchema.name;
    }
    return 'Any entity';
  }
}

function MaterialInputCompatibilityTooltip(props: {
  isCompatible: boolean;
  materialOutputSetting: ProcedureMaterialOutputSetting;
  materialInputSetting: ProcedureMaterialInputSetting;
}): JSX.Element {
  return props.isCompatible ? (
    <div>
      <BenchlingIcon icon="check-circle" className={css(styles.iconCheck)} />
      <b>Material types match</b>
    </div>
  ) : (
    <div>
      <BenchlingIcon icon="exclamation-triangle" className={css(styles.iconWarning)} />
      <b>Material types do not match</b>
      <br />
      {`The type of the destination material input (${getMaterialDataTypeText(
        props.materialInputSetting
      )}) will be overridden to be the same as the source material output (${getMaterialDataTypeText(
        props.materialOutputSetting
      )}).`}
    </div>
  );
}

export function ProcedureMaterialInputHandle(props: ProcedureMaterialInputHandleProps): JSX.Element {
  const {handlesInUse, materialOutputSettingBeingConnected} = ProcedureEditorContext.useContext();
  const [isHovered, hoverProps] = useHoverState();
  const {materialInputSetting} = props;
  const isThisHandleConnectedAlready = handlesInUse.includes(props.id);
  const isMaterialEdgeDrawInFlight = materialOutputSettingBeingConnected != null;

  const isCompatible = isMaterialConnectionCompatible(
    materialOutputSettingBeingConnected,
    materialInputSetting
  );
  const showHandleCompatibilityStatus = !isThisHandleConnectedAlready && isMaterialEdgeDrawInFlight;
  const showTooltip = isHovered && showHandleCompatibilityStatus;
  const tooltipContents = showTooltip ? (
    <MaterialInputCompatibilityTooltip
      isCompatible={isCompatible}
      materialOutputSetting={materialOutputSettingBeingConnected}
      materialInputSetting={materialInputSetting}
    />
  ) : undefined;

  const handleClass = css(
    isMaterialEdgeDrawInFlight ? styles.materialInputHandle : styles.handle,
    !props.isVisible && styles.invisibleHandle,
    // Incompatible materials (when relevant)
    !isCompatible && showHandleCompatibilityStatus && styles.incompatibleMaterialInputHandle,
    // Compatible materials (when relevant)
    isCompatible && showHandleCompatibilityStatus && styles.compatibleMaterialInputHandle,
    isThisHandleConnectedAlready && styles.connectedMaterialInputHandle
  );

  return (
    <Tooltip text={tooltipContents} isDisabled={!showTooltip} placement={FloatingPlacement.RIGHT_START}>
      <div className={css(isMaterialEdgeDrawInFlight && styles.tooltipHoverZone)} {...hoverProps}>
        <Handle
          type="target"
          position={Position.Left}
          className={handleClass}
          isValidConnection={(connection) => isValidConnection(connection, handlesInUse)}
          id={props.id}
          {...getTestProps({component: 'ProcedureMaterialInputHandle', element: 'handle'})}
        />
      </div>
    </Tooltip>
  );
}

export function ProcedureNodeHandle(props: ProcedureGenericHandleProps): JSX.Element {
  const {handlesInUse} = ProcedureEditorContext.useContext();

  const handleClass = css(
    styles.handle,
    !props.isVisible && styles.invisibleHandle,
    handlesInUse.includes(props.id) && styles.connectedHandle
  );

  return (
    <Handle
      type={props.type}
      position={props.type === 'source' ? Position.Right : Position.Left}
      className={handleClass}
      isValidConnection={(connection) => isValidConnection(connection, handlesInUse)}
      id={props.id}
    />
  );
}

const styles = StyleSheet.create({
  tooltipHoverZone: {
    width: 50,
    height: 20,
    margin: '-19px 0 0 -50px',
    zIndex: nodeZIndex + 2,
    position: 'relative',
  },
  materialInputHandle: {
    display: 'block',
    width: 10,
    height: 10,
    top: 10,
    left: 36,
    border: `1px solid ${Colors.GRAY_DARK}`,
    background: Colors.WHITE,
    borderRadius: '50%',
    position: 'absolute',
  },
  handle: {
    width: 10,
    height: 10,
    margin: '0 -1px',
    border: `1px solid ${Colors.GRAY_DARK}`,
    background: Colors.WHITE,
    borderRadius: '50%',
    zIndex: nodeZIndex + 2,
  },
  invisibleHandle: {
    visibility: 'hidden',
  },
  connectedHandle: {
    background: Colors.GRAY_DARK,
  },
  connectedMaterialInputHandle: {
    background: Colors.GRAY_DARK,
    visibility: 'visible',
  },
  invalidHandle: {
    background: Colors.WHITE,
    border: `1px solid ${Colors.RED}`,
    visibility: 'visible',
  },
  compatibleMaterialInputHandle: {
    background: Colors.WHITE,
    border: `1px solid ${Colors.BLUE_LIGHT}`,
    visibility: 'visible',
  },
  incompatibleMaterialInputHandle: {
    background: Colors.WHITE,
    border: `1px solid ${Colors.ORANGE}`,
    visibility: 'visible',
  },
  iconCheck: {
    color: Colors.GREEN,
    marginRight: '0.5em',
  },
  iconWarning: {
    color: Colors.ORANGE,
    marginRight: '0.5em',
  },
});
